import { useState } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';

import {
  CustomerReduced,
  ListCustomersQuery,
} from '@/core/domain/people/Customers/Customers.types';
import { useCustomerReducedList } from '@/core/domain/people/Customers/Customers.hooks';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { applyMask } from '@/utils/mask';
import { cnpjMask, cpfMask } from '@/utils/mask/commonMasks';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import AppPageHeader from '@/components/_common/AppPageHeader';
import CustomersExtraFilters from '@/components/Customers/CustomersExtraFilters';

import { useDebounce } from '@/hooks';

interface ModalCustomersListProps {
  visible: boolean;
  onSelectCustomerId: (customerId?: number) => void;
}

const ModalCustomersList = ({
  visible,
  onSelectCustomerId,
}: ModalCustomersListProps) => {
  const [queryParams, setQueryParams] = useState<ListCustomersQuery>({
    order: 'ASC',
    active: true,
    activeVehicle: true,
  });

  const queryDebounce = useDebounce(queryParams);

  const {
    data: customersData,
    isFetching: isFetchingCustomers,
    fetchNextPage,
    hasNextPage,
  } = useCustomerReducedList(queryDebounce, visible);

  const customers = flatInfiniteData(customersData);

  const customersColumns: ColumnsType<CustomerReduced> = [
    {
      dataIndex: 'cpfCnpj',
      title: <FormattedMessage id="customers.list.header.cpfCnpj" />,
      render: (value) =>
        value
          ? applyMask(value, value && value.length === 11 ? cpfMask : cnpjMask)
          : '-',
    },
    {
      dataIndex: 'foreignDocument',
      title: 'Documento estrangeiro',
    },
    {
      dataIndex: 'name',
      title: <FormattedMessage id="customers.list.header.name" />,
      ellipsis: true,
    },
    {
      dataIndex: 'actions',
      align: 'right',
      render: (_, row) => (
        <Button type="primary" onClick={() => onSelectCustomerId(row.id)}>
          <FormattedMessage id="diagnostics.modal.button.select" />
        </Button>
      ),
    },
  ];

  return (
    <>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        extraFilters={
          <CustomersExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<CustomerReduced>
        columns={customersColumns}
        dataSource={customers}
        loading={isFetchingCustomers}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </>
  );
};

export default ModalCustomersList;
